import { useState } from "react";
import axios from "axios";
import {
    Mail,
    Phone,
    User,
    MessageSquare,
    FileText,
    ArrowRight,
    ShieldCheck,
    CheckCircle2,
    AlertCircle,
} from "lucide-react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

export default function ContactForm() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
    });

    const [success, setSuccess] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const { executeRecaptcha } = useGoogleReCaptcha();

    const handleChange = (e) => {
        setForm({
            ...form,
            [e.target.name]: e.target.value,
        });

        setError("");
        setSuccess("");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (
            !form.name.trim() ||
            !form.email.trim() ||
            !form.phone.trim() ||
            !form.message.trim()
        ) {
            setError("Please fill all required fields.");
            return;
        }

        setLoading(true);
        setError("");
        setSuccess("");

        try {
            if (!executeRecaptcha) {
                setError(
                    "reCAPTCHA is not loaded. Please refresh the page and try again."
                );
                setLoading(false);
                return;
            }

            const token = await executeRecaptcha("contact_form");

            const res = await axios.post(
                "https://marinadubai.com/api/contact",
                {
                    ...form,
                    recaptcha_token: token,
                }
            );

            if (res.data.success) {
                setSuccess(
                    "Your message has been sent successfully. Our team will get back to you shortly."
                );

                setForm({
                    name: "",
                    email: "",
                    phone: "",
                    subject: "",
                    message: "",
                });
            } else {
                setError(
                    res.data.message || "Failed to send your message."
                );
            }
        } catch (err) {
            setError(
                err.response?.data?.message ||
                "Something went wrong. Please try again."
            );
        } finally {
            setLoading(false);
        }
    };

    const inputClass =
        "w-full rounded-2xl border border-[#DCE7E4] bg-[#F8FAF8] py-4 pl-12 pr-4 text-sm font-medium text-[#173B39] placeholder:text-[#8A9B98] outline-none transition-all duration-300 focus:border-[#0B5552] focus:bg-white focus:ring-4 focus:ring-[#0B5552]/10";

    return (
        <div className="w-full bg-[#F7F5EF]">

            {/* =========================================================
                CONTACT FORM
            ========================================================== */}

            <div className="relative overflow-hidden rounded-[30px] border border-[#DCE7E4] bg-white shadow-[0_20px_60px_rgba(11,85,82,0.08)]">

                {/* Top Accent */}
                <div className="h-1.5 w-full bg-gradient-to-r from-[#0B5552] via-[#0B5552] to-[#D4A83F]" />

                {/* Decorative Circle */}
                <div className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-[#0B5552]/[0.04]" />

                <div className="pointer-events-none absolute -bottom-32 -left-32 h-72 w-72 rounded-full bg-[#D4A83F]/[0.06]" />

                <div className="relative p-6 sm:p-8 lg:p-10 xl:p-12">

                    {/* =================================================
                        HEADER
                    ================================================== */}

                    <div className="mb-8">

                        <div className="inline-flex items-center gap-2 rounded-full border border-[#0B5552]/10 bg-[#0B5552]/[0.06] px-4 py-2 text-[10px] font-extrabold uppercase tracking-[0.18em] text-[#0B5552]">

                            <span className="h-1.5 w-1.5 rounded-full bg-[#D4A83F]" />

                            Send An Enquiry

                        </div>

                        <h2 className="mt-4 text-3xl font-extrabold leading-tight tracking-tight text-[#173B39] sm:text-4xl">

                            How Can We Help?

                        </h2>

                        <p className="mt-3 max-w-xl text-sm leading-6 text-[#70817E] sm:text-base">

                            Have a question about your stay or travel plans?
                            Send us a message and our team will be happy to
                            assist you.

                        </p>

                    </div>


                    {/* =================================================
                        SUCCESS MESSAGE
                    ================================================== */}

                    {success && (
                        <div className="mb-7 flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4">

                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-100">

                                <CheckCircle2
                                    size={19}
                                    className="text-emerald-600"
                                />

                            </div>

                            <div>

                                <p className="text-sm font-extrabold text-emerald-700">
                                    Message Sent Successfully
                                </p>

                                <p className="mt-1 text-xs leading-5 text-emerald-600">
                                    {success}
                                </p>

                            </div>

                        </div>
                    )}


                    {/* =================================================
                        ERROR MESSAGE
                    ================================================== */}

                    {error && (
                        <div className="mb-7 flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-4">

                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-100">

                                <AlertCircle
                                    size={19}
                                    className="text-red-600"
                                />

                            </div>

                            <div>

                                <p className="text-sm font-extrabold text-red-700">
                                    Unable to Send Message
                                </p>

                                <p className="mt-1 text-xs leading-5 text-red-600">
                                    {error}
                                </p>

                            </div>

                        </div>
                    )}


                    {/* =================================================
                        FORM
                    ================================================== */}

                    <form
                        onSubmit={handleSubmit}
                        className="space-y-5"
                    >

                        {/* Name + Email */}

                        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">

                            {/* Name */}

                            <div>

                                <label className="mb-2 block text-xs font-extrabold uppercase tracking-wide text-[#284D4A]">
                                    Your Name
                                    <span className="ml-1 text-red-500">*</span>
                                </label>

                                <div className="relative">

                                    <User
                                        size={17}
                                        strokeWidth={2}
                                        className="absolute left-4 top-1/2 -translate-y-1/2 text-[#78908C]"
                                    />

                                    <input
                                        type="text"
                                        name="name"
                                        value={form.name}
                                        onChange={handleChange}
                                        placeholder="Enter your name"
                                        className={inputClass}
                                    />

                                </div>

                            </div>


                            {/* Email */}

                            <div>

                                <label className="mb-2 block text-xs font-extrabold uppercase tracking-wide text-[#284D4A]">
                                    Email Address
                                    <span className="ml-1 text-red-500">*</span>
                                </label>

                                <div className="relative">

                                    <Mail
                                        size={17}
                                        strokeWidth={2}
                                        className="absolute left-4 top-1/2 -translate-y-1/2 text-[#78908C]"
                                    />

                                    <input
                                        type="email"
                                        name="email"
                                        value={form.email}
                                        onChange={handleChange}
                                        placeholder="info@southtravels.com"
                                        className={inputClass}
                                    />

                                </div>

                            </div>

                        </div>


                        {/* Phone + Subject */}

                        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">

                            {/* Phone */}

                            <div>

                                <label className="mb-2 block text-xs font-extrabold uppercase tracking-wide text-[#284D4A]">
                                    Phone Number
                                    <span className="ml-1 text-red-500">*</span>
                                </label>

                                <div className="relative">

                                    <Phone
                                        size={17}
                                        strokeWidth={2}
                                        className="absolute left-4 top-1/2 -translate-y-1/2 text-[#78908C]"
                                    />

                                    <input
                                        type="tel"
                                        name="phone"
                                        value={form.phone}
                                        onChange={handleChange}
                                        placeholder="+971 4 447 3839"
                                        className={inputClass}
                                    />

                                </div>

                            </div>


                            {/* Subject */}

                            <div>

                                <label className="mb-2 block text-xs font-extrabold uppercase tracking-wide text-[#284D4A]">
                                    Subject
                                </label>

                                <div className="relative">

                                    <FileText
                                        size={17}
                                        strokeWidth={2}
                                        className="absolute left-4 top-1/2 -translate-y-1/2 text-[#78908C]"
                                    />

                                    <input
                                        type="text"
                                        name="subject"
                                        value={form.subject}
                                        onChange={handleChange}
                                        placeholder="How can we help?"
                                        className={inputClass}
                                    />

                                </div>

                            </div>

                        </div>


                        {/* Message */}

                        <div>

                            <label className="mb-2 block text-xs font-extrabold uppercase tracking-wide text-[#284D4A]">
                                Your Message
                                <span className="ml-1 text-red-500">*</span>
                            </label>

                            <div className="relative">

                                <MessageSquare
                                    size={17}
                                    strokeWidth={2}
                                    className="absolute left-4 top-4 text-[#78908C]"
                                />

                                <textarea
                                    name="message"
                                    rows="6"
                                    value={form.message}
                                    onChange={handleChange}
                                    placeholder="Tell us how we can help..."
                                    className={`${inputClass} resize-none py-3.5`}
                                />

                            </div>

                        </div>


                        {/* =================================================
                            SUBMIT AREA
                        ================================================== */}

                        <div className="flex flex-col gap-5 border-t border-[#E6ECEA] pt-6 sm:flex-row sm:items-center sm:justify-between">

                            {/* Security */}

                            <div className="flex items-center gap-3">

                                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#0B5552]/[0.07]">

                                    <ShieldCheck
                                        size={17}
                                        className="text-[#0B5552]"
                                    />

                                </div>

                                <p className="text-[11px] leading-5 text-[#849390]">

                                    Your information is secure.
                                    <br />

                                    We usually respond within 24 hours.

                                </p>

                            </div>


                            {/* Submit */}

                            <button
                                type="submit"
                                disabled={loading}
                                className="
                                    group
                                    flex
                                    min-h-[52px]
                                    w-full
                                    items-center
                                    justify-center
                                    gap-3
                                    rounded-2xl
                                    bg-[#0B5552]
                                    px-8
                                    py-3.5
                                    text-sm
                                    font-extrabold
                                    text-white
                                    shadow-[0_12px_28px_rgba(11,85,82,0.20)]
                                    transition-all
                                    duration-300
                                    hover:-translate-y-0.5
                                    hover:bg-[#084744]
                                    hover:shadow-[0_16px_35px_rgba(11,85,82,0.25)]
                                    disabled:cursor-not-allowed
                                    disabled:opacity-60
                                    sm:w-auto
                                "
                            >

                                {loading ? (
                                    <>
                                        <span className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />

                                        Sending...
                                    </>
                                ) : (
                                    <>
                                        Send Message

                                        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white/10">

                                            <ArrowRight
                                                size={16}
                                                className="transition-transform duration-300 group-hover:translate-x-1"
                                            />

                                        </span>
                                    </>
                                )}

                            </button>

                        </div>

                    </form>

                </div>

            </div>

        </div>
    );
}